import { Stack, TextInput, Textarea } from '@mantine/core';
import { useEffect, useState } from 'react';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { DataField, ExtractionFieldType, MissingValue } from '@/types/automationFields.ts';

type MissingValueCellProps = {
  field: DataField;
  update: (field: Partial<DataField>) => void;
};

type Choice =
  | 'type_empty'
  | 'null'
  | 'empty_string'
  | 'empty_list'
  | 'empty_object'
  | 'text'
  | 'number'
  | 'true'
  | 'false'
  | 'json';

const typeEmptyLabel: Record<ExtractionFieldType, string> = {
  [ExtractionFieldType.Text]: 'Empty string',
  [ExtractionFieldType.Number]: 'No numeric value',
  [ExtractionFieldType.Boolean]: 'False',
  [ExtractionFieldType.List]: 'Empty list',
  [ExtractionFieldType.Object]: 'Empty object',
};

function legacyChoice(
  fieldType: ExtractionFieldType,
  missingValue: DataField['missing_value'],
  defaultValue: string | null,
): Choice {
  if (missingValue) {
    if (missingValue.kind === 'type_empty' || missingValue.kind === 'null') {
      return missingValue.kind;
    }
    if (fieldType === ExtractionFieldType.Boolean) {
      return missingValue.value ? 'true' : 'false';
    }
    if (fieldType === ExtractionFieldType.Text && missingValue.value === '') return 'empty_string';
    if (fieldType === ExtractionFieldType.List && Array.isArray(missingValue.value) && missingValue.value.length === 0) {
      return 'empty_list';
    }
    if (
      fieldType === ExtractionFieldType.Object &&
      !Array.isArray(missingValue.value) &&
      typeof missingValue.value === 'object' &&
      Object.keys(missingValue.value).length === 0
    ) {
      return 'empty_object';
    }
    if (fieldType === ExtractionFieldType.Text) return 'text';
    if (fieldType === ExtractionFieldType.Number) return 'number';
    return 'json';
  }

  // Preserve legacy configuration until the user makes a change.
  if (defaultValue == null || defaultValue === '') return 'type_empty';
  if (fieldType === ExtractionFieldType.Boolean) {
    return defaultValue.toLowerCase() === 'true' ? 'true' : 'false';
  }
  return fieldType === ExtractionFieldType.Text ? 'text' : fieldType === ExtractionFieldType.Number ? 'number' : 'json';
}

function inputValue(missingValue: DataField['missing_value'], defaultValue: string | null): string {
  if (missingValue?.kind === 'value') {
    return typeof missingValue.value === 'string' ? missingValue.value : JSON.stringify(missingValue.value);
  }
  return defaultValue ?? '';
}

export default function MissingValueCell({ field, update }: MissingValueCellProps) {
  const [choice, setChoice] = useState<Choice>(() => legacyChoice(field.field_type, field.missing_value, field.default_value));
  const [value, setValue] = useState(() => inputValue(field.missing_value, field.default_value));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setChoice(legacyChoice(field.field_type, field.missing_value, field.default_value));
    setValue(inputValue(field.missing_value, field.default_value));
    setError(null);
  }, [field.field_id, field.field_type, field.missing_value, field.default_value]);

  if (field.is_required) return <span>—</span>;

  const options = [
    { value: 'type_empty', label: `No default (${typeEmptyLabel[field.field_type].toLowerCase()})` },
    { value: 'null', label: 'Null' },
    ...(field.field_type === ExtractionFieldType.Text
      ? [
          { value: 'empty_string', label: 'Default: empty string' },
          { value: 'text', label: 'Custom value' },
        ]
      : field.field_type === ExtractionFieldType.List
        ? [
            { value: 'empty_list', label: 'Default: empty list' },
            { value: 'json', label: 'Custom value' },
          ]
        : field.field_type === ExtractionFieldType.Object
          ? [
              { value: 'empty_object', label: 'Default: empty object' },
              { value: 'json', label: 'Custom value' },
            ]
      : field.field_type === ExtractionFieldType.Boolean
      ? [
          { value: 'true', label: 'True' },
          { value: 'false', label: 'False' },
        ]
      : [{ value: 'number', label: 'Custom value' }]),
  ];

  const persist = (nextChoice: Choice, rawValue: string) => {
    let missingValue: MissingValue;
    try {
      if (nextChoice === 'type_empty' || nextChoice === 'null') {
        missingValue = { kind: nextChoice };
      } else if (nextChoice === 'empty_string') {
        missingValue = { kind: 'value', value: '' };
      } else if (nextChoice === 'empty_list') {
        missingValue = { kind: 'value', value: [] };
      } else if (nextChoice === 'empty_object') {
        missingValue = { kind: 'value', value: {} };
      } else if (nextChoice === 'true' || nextChoice === 'false') {
        missingValue = { kind: 'value', value: nextChoice === 'true' };
      } else if (nextChoice === 'text') {
        missingValue = { kind: 'value', value: rawValue };
      } else if (nextChoice === 'number') {
        if (rawValue.trim() === '') throw new Error('Enter a finite number');
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue)) throw new Error('Enter a finite number');
        missingValue = { kind: 'value', value: numericValue };
      } else {
        const parsed = JSON.parse(rawValue);
        const isList = field.field_type === ExtractionFieldType.List;
        if (isList ? !Array.isArray(parsed) : !parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
          throw new Error(isList ? 'Enter a JSON list' : 'Enter a JSON object');
        }
        missingValue = { kind: 'value', value: parsed };
      }
      setError(null);
      update({ default_value: null, missing_value: missingValue });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Enter a valid value');
    }
  };

  const customChoice = choice === 'text' || choice === 'number' || choice === 'json';
  return (
    <Stack gap={4} miw={170}>
      <StyledSelect
        aria-label="When source is missing"
        data={options}
        value={choice}
        onChange={(next) => {
          const nextChoice = next as Choice;
          setChoice(nextChoice);
          if (!['text', 'number', 'json'].includes(nextChoice)) persist(nextChoice, value);
        }}
        clearable={false}
        allowDeselect={false}
      />
      {customChoice && (choice === 'json' ? (
        <Textarea
          aria-label="Custom missing value"
          value={value}
          onChange={(event) => { setValue(event.currentTarget.value); persist(choice, event.currentTarget.value); }}
          error={error}
          minRows={2}
          placeholder={field.field_type === ExtractionFieldType.List ? '[]' : '{}'}
        />
      ) : (
        <TextInput
          aria-label="Custom missing value"
          value={value}
          onChange={(event) => { setValue(event.currentTarget.value); persist(choice, event.currentTarget.value); }}
          error={error}
          placeholder={choice === 'number' ? '0' : 'Value'}
        />
      ))}
    </Stack>
  );
}
